import { motion } from "framer-motion"
import { Users, Heart, Zap, Target, Headphones, Globe2Icon, ChartBar, Rocket } from "lucide-react"
import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PricingPlanModal } from "../components/PricingPlanModal";
import { getPlanList } from "../redux/actions";
import { DistributionModal } from "../components/DistributionAccess/DistributionModal";
import { generateMetadata } from "../seo/metadataGenerator";
import { SITE_URL, PAGE_OG_IMAGES } from "../seo/constants";
import { generateBreadcrumbSchema, generateFAQPageSchema, generateGraphSchema, generateServiceSchema, generateSoftwareApplicationSchema, generateWebPageSchema } from "../seo/schemaGenerator";
import SeoHead from "../seo/SeoHead";
import { isPrerendering } from "../helper/isPrerendering";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function Distribution() {
    usePrerenderReady();
    const dispatch = useDispatch()
    const [showDistribution, setShowDistribution] = useState(false)
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
    const [isHovering, setIsHovering] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { hasActiveSubscription, subscriptionPlan } = useSelector((state) => state.billing);
    const planList = subscriptionPlan || [];
    const lastSubscriptionFetched = subscriptionPlan?.lastFetched;
    const token = localStorage.getItem("authToken");

    const updateCursorPosition = useCallback((e) => {
        requestAnimationFrame(() => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
        });
    }, []);

    useEffect(() => {
        document.body.style.cursor = "none";
        const handleMouseEnter = () => setIsHovering(true);
        const handleMouseLeave = () => setIsHovering(false);

        const interactiveElements = document.querySelectorAll(
            'a, button, input, [role="button"]'
        );
        interactiveElements.forEach((element) => {
            element.addEventListener("mouseenter", handleMouseEnter);
            element.addEventListener("mouseleave", handleMouseLeave);
        });

        window.addEventListener("mousemove", updateCursorPosition);

        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 3000);

        return () => {
            clearTimeout(timer);
            document.body.style.cursor = "auto";
            window.removeEventListener("mousemove", updateCursorPosition);
            interactiveElements.forEach((element) => {
                element.removeEventListener("mouseenter", handleMouseEnter);
                element.removeEventListener("mouseleave", handleMouseLeave);
            });
        };
    }, [updateCursorPosition]);

    const hasFetched = useRef(false);
    useEffect(() => {
        const fetchPlans = async () => {
            const shouldFetch =
                !planList?.length ||
                !lastSubscriptionFetched ||
                Date.now() - lastSubscriptionFetched > 1000 * 60 * 30; // 30 mins cache

            if (shouldFetch && !hasFetched.current && !isPrerendering() && !!token) {
                hasFetched.current = true; // prevent duplicate fetches
                await dispatch(getPlanList("subscription"));
            }
        };

        fetchPlans();
        return () => {
            hasFetched.current = false;
        };
    }, [dispatch, planList?.length, lastSubscriptionFetched]);

    //SEO
    const metadata = generateMetadata({
        title:
            "Music Distribution for Independent Artists | UpNComer",

        description:
            "Distribute music globally, manage releases, track performance and grow your audience with UpNComer’s AI-powered music distribution platform.",

        image: PAGE_OG_IMAGES.distribution,

        path: "/distribution",
    })

    const serviceSchema = generateServiceSchema({
        title:
            "Music Distribution for Independent Artists | UpNComer",

        description:
            "Distribute music globally, manage releases, track performance and grow your audience with UpNComer’s AI-powered music distribution platform.",

        url: `${SITE_URL}/distribution`,
    })

    const breadcrumbSchema = generateBreadcrumbSchema([
        {
            "name": "Home",
            "path": "/"
        },
        {
            "name": "Distribution",
            "path": "/distribution"
        }
    ])

    const schemas = generateGraphSchema([
        generateWebPageSchema({
            title:
                "Music Distribution for Independent Artists | UpNComer",

            description:
                "Distribute music globally, manage releases, track performance and grow your audience with UpNComer’s AI-powered music distribution platform.",

            url: `${SITE_URL}/distribution`,
        }),
        generateFAQPageSchema(),
        serviceSchema,
        generateSoftwareApplicationSchema(),
        breadcrumbSchema
    ])

    return (
        <>
            <SeoHead
                metadata={metadata}
                schemas={schemas}
            />
            <div
                className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
                style={{
                    left: `${cursorPosition.x}px`,
                    top: `${cursorPosition.y}px`,
                    transform: `translate(-50%, -50%)`,
                }}
            >
                <div className="inner-circle"></div>
            </div>
            {/* <div
        className={`transition-all duration-1000 ${isLoading ? "opacity-0 blur-xl" : "opacity-100 blur-none"
          }`}
      > */}
            <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
                {/* Background elements */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />
                    <motion.div
                        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
                        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                    />
                </div>
                {/* Enhanced Multi-layered Background */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

                    {/* Floating orbs */}
                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.3, 0.6, 0.3],
                        }}
                        transition={{
                            duration: 10,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                        }}
                        className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                    />

                    <motion.div
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.2, 0.5, 0.2],
                        }}
                        transition={{
                            duration: 12,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                            delay: 3,
                        }}
                        className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
                    />
                </div>

                <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
                    <div className="container max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-center mb-8"
                        >
                            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                                Music Distribution, Rebuilt for Independent Artists
                            </h1>
                            <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed">
                                Release your music globally, keep full control, and turn every drop into real growth with UpNComer.
                            </p>
                            <button
                                onClick={() => setShowDistribution(true)}
                                className="inline-block bg-white text-theme_clr2 text-center
                             px-8 py-4 text-lg font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10 mt-8"
                                style={{
                                    boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                                }}
                            >
                                DISTRIBUTE YOUR MUSIC
                            </button>
                        </motion.div>

                        <div className="space-y-16">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-8"
                                style={{
                                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                }}
                            >
                                <div className="flex items-start space-x-6">
                                    <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                                        <Target className="w-8 h-8 text-[#FA6138]" />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                                            THE PROBLEM
                                        </h2>
                                        <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                                            Distribution Shouldn’t Limit Your Growth
                                        </p>
                                        <p className="text-[#818cf8]/80 leading-relaxed">
                                            Most distribution platforms were built to deliver your music, not grow your career.<br />
                                            They get your songs live — and leave the rest up to you.<br />
                                            That means no real strategy, no meaningful insights, and no system to help you scale.<br />
                                            You shouldn’t just be releasing music.<br />
                                            You should be building momentum around every release.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#4f46e5]/20 rounded-2xl p-8"
                                style={{
                                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                }}
                            >
                                <div className="flex items-start space-x-6">
                                    <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                                        <Heart className="w-8 h-8 text-[#FA6138]" />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                                            THE SOLUTION
                                        </h2>
                                        <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                                            Distribution That Works With Everything Else
                                        </p>
                                        <p className="text-[#818cf8]/80 leading-relaxed">
                                            UpNComer’s distribution does more than put your music on platforms — it plugs directly into your growth engine.
                                            <br /><br />
                                            Every release connects to your:
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li>Campaigns</li>
                                                <li>Fanbase</li>
                                                <li>Content</li>
                                                <li>Data</li>
                                            </ul>
                                            <br />
                                            So you’re not just uploading tracks.
                                            <br />
                                            You’re launching them with purpose.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                                className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#818cf8]/20 rounded-2xl p-8"
                                style={{
                                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                }}
                            >
                                <div className="flex items-start space-x-6">
                                    <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                                        <Zap className="w-8 h-8 text-[#FA6138]" />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                                            HOW IT WORKS
                                        </h2>
                                        <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                                            One Platform. Full Control. Global Reach.
                                        </p>
                                        <p className="text-[#818cf8]/80 leading-relaxed">
                                            Upload your music once and distribute it across hundreds of platforms worldwide.
                                            <br /><br />
                                            Every release comes with:
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li>Built-in analytics</li>
                                                <li>Campaign-ready integrations</li>
                                                <li>Smart links and fan capture</li>
                                                <li>Real-time performance tracking</li>
                                            </ul>
                                            <br />
                                            No extra tools. No disconnected workflows.
                                            <br />
                                            Just one system where everything works together.
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-6">
                                    {[
                                        {
                                            value: "479+",
                                            icon: <Headphones className="w-8 h-8 text-[#FA6138]" />,
                                            label: "479+ Platforms",
                                            bgColor: "bg-[#3b82f6]/15",
                                            borderColor: "border-[#3b82f6]/20",
                                        },
                                        {
                                            value: "2.3M+",
                                            icon: <Globe2Icon className="w-8 h-8 text-[#FA6138]" />,
                                            label: "Global Reach",
                                            bgColor: "bg-[#3b82f6]/15",
                                            borderColor: "border-[#1e3a8a]/25",
                                        },
                                        {
                                            value: "$50K+",
                                            icon: <ChartBar className="w-8 h-8 text-[#FA6138]" />,
                                            label: "Real-Time Data",
                                            bgColor: "bg-[#3b82f6]/15",
                                            borderColor: "border-[#60a5fa]/20",
                                        },
                                        {
                                            value: "$50K+",
                                            icon: <Rocket className="w-8 h-8 text-[#FA6138]" />,
                                            label: "Campaign Ready",
                                            bgColor: "bg-[#3b82f6]/15",
                                            borderColor: "border-[#60a5fa]/20",
                                        },
                                    ].map((stat, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
                                            className={`flex flex-col justify-center items-center ${stat.bgColor} backdrop-blur-xl border ${stat.borderColor} rounded-2xl p-8 text-center hover:bg-[#3b82f6]/10 transition-all duration-300`}
                                            style={{
                                                boxShadow: "0 10px 30px rgba(30, 58, 138, 0.2), inset 0 1px 0 rgba(59, 130, 246, 0.1)",
                                            }}
                                        >
                                            {/* <div className="text-4xl font-bold text-[#FA6138] mb-2 drop-shadow-lg">{stat.value}</div> */}
                                            <div className={`p-2`}>
                                                {stat.icon}
                                            </div>
                                            <div className="text-[#93c5fd] font-light">{stat.label}</div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.8 }}
                                className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#a5b4fc]/20 rounded-2xl p-8"
                                style={{
                                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                }}
                            >
                                <div className="flex items-start space-x-6">
                                    <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                                        <Users className="w-8 h-8 text-[#FA6138]" />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                                            DIFFERENTIATION
                                        </h2>
                                        <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                                            More Than Distribution — It’s Your Infrastructure
                                        </p>
                                        <p className="text-[#818cf8]/80 leading-relaxed">
                                            Traditional distributors stop at delivery.<br />
                                            UpNComer goes further — placing your music not just on streaming platforms, but across <strong>airlines, gyms, cafés, restaurants, and more,</strong> maximizing your earning potential.<br />
                                            <br />
                                            We keep things simple:
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li><strong>No subscription fees for distribution</strong></li>
                                                <li><strong>Only a 6% royalty split</strong></li>
                                                <li><strong>You retain 100% ownership, rights, and control</strong></li>
                                            </ul>
                                            <br />
                                            We only earn when you do — aligned with your growth.
                                        </p>
                                        <br />
                                        <p className="text-[#818cf8]/80 leading-relaxed">
                                            <strong className="text-[#c7d2fe] text-lg leading-relaxed">🚀 Bring Your Catalog. Unlock New Growth.<br /><br /></strong>
                                            Already distributed elsewhere? No problem.<br />
                                            You can <strong>move your music to UpNComer seamlessly</strong> and instantly unlock new opportunities.<br />
                                            <br />
                                            When you transfer your catalog, you get the ability to:
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li><strong>Re-pitch your music with priority</strong> to platforms where you’re already distributed</li>
                                                <li><strong>Expand into platforms your music has never reached before</strong></li>
                                                <li>Relaunch your tracks with <strong>campaigns, data, and fan capture built in</strong></li>
                                                <li>Your existing catalog doesn’t just move — <strong>it gets a second life.</strong></li>
                                            </ul>
                                            <br />
                                            Beyond distribution, UpNComer connects everything that drives your career:
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li>Launch campaigns the moment your music drops</li>
                                                <li>Track performance instantly across platforms</li>
                                                <li>Turn listeners into fans — and fans into long-term growth</li>
                                            </ul>
                                            <br />
                                            Your catalog isn’t just uploaded.<br />
                                            <strong>It becomes your engine.</strong><br />
                                            <strong>Distribution is no longer a tool. It’s your growth infrastructure.</strong>
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="text-center"
                        >
                            <h2 className="text-3xl font-bold text-[#c7d2fe] mt-8 mb-6">Ready to Start Releasing With UpNComer?</h2>
                            <p className="text-[#818cf8]/80 mb-8 max-w-2xl mx-auto">
                                Request access to our distribution portal and start building your catalog the right way.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={() => setShowDistribution(true)}
                                    className="inline-block bg-theme_clr4 text-white text-center
                             px-8 py-4 text-lg font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10"
                                    style={{
                                        boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                                    }}
                                >
                                    REQUEST DISTRIBUTION ACCESS
                                </button>
                                {/* <a
                                    href="https://www.instagram.com/upncomer.co/"
                                    className="inline-flex items-center justify-center bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] 
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40
                         transition-all duration-300 backdrop-blur-xl shadow-lg hover:shadow-xl"
                                >
                                    <span>FOLLOW THE BUILD <i class="ml-2 bi bi-wrench"></i></span>
                                </a> */}
                            </div>
                        </motion.div>
                    </div>
                </main>
            </div>
            {/* </div> */}
            {/* Distribution Modal */}
            <DistributionModal
                isOpen={showDistribution}
                onClose={() => setShowDistribution(false)}
            />
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/dashboard"} />
        </>
    )
}
